import React, { useState, useEffect } from 'react';
import {
    Card,
    Form,
    Input,
    Button,
    Table,
    Space,
    Modal,
    Select,
    Upload,
    Switch,
    Tag,
    message,
    Row,
    Col,
    Avatar,
    Tooltip,
    Dropdown,
    Grid
} from 'antd';
import {
    PlusOutlined,
    UploadOutlined,
    EditOutlined,
    DeleteOutlined,
    ShopOutlined,
    GlobalOutlined,
    EnvironmentOutlined,
    StarOutlined,
    MailOutlined,
    PhoneOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    MoreOutlined
} from '@ant-design/icons';
import axios from "axios";
import { BASE_URL } from "../../Constants";
import { useTranslation } from "react-i18next";
import EntityCardList from "./EntityCardList";

const { TextArea } = Input;

const ExhibitorManagement = ({
    eventId,
    exhibitors: initialExhibitors,
    onUpdate,
    isCreateMode = false,
    onExhibitorsChange,
}) => {
    // `=== false` so the first render (before antd reports breakpoints) defaults to desktop.
    const isMobile = Grid.useBreakpoint().md === false;
    const { t } = useTranslation();
    const [modalVisible, setModalVisible] = useState(false);
    const [editingExhibitor, setEditingExhibitor] = useState(null);
    const [form] = Form.useForm();
    const [exhibitors, setExhibitors] = useState(initialExhibitors || []);
    const [checkIns, setCheckIns] = useState([]); // <--- NEW STATE

    const hasValidEventId =
        typeof eventId === "string" &&
        eventId.trim() !== "" &&
        eventId !== "undefined" &&
        eventId !== "null";
    const canPersistToBackend = !isCreateMode && hasValidEventId;

    useEffect(() => {
        const fetchCheckIns = async () => {
            try {
                const response = await axios.get(`${BASE_URL}/CheckIn/event/${eventId}`);
                if (response.data && response.data.items) {
                    setCheckIns(response.data.items);
                }
            } catch (error) {
                console.error("Error fetching check-ins:", error);
            }
        };
        if (canPersistToBackend) fetchCheckIns();
    }, [canPersistToBackend, eventId]);

    useEffect(() => {
        setExhibitors(initialExhibitors || []);
    }, [initialExhibitors]);

    const handleAddExhibitor = () => {
        setEditingExhibitor(null);
        form.resetFields();
        setModalVisible(true);
    };

    const saveExhibitorsToBackend = async (updatedExhibitors) => {
        if (!canPersistToBackend) {
            throw new Error("Event must be created before saving exhibitors to backend");
        }

        try {
            const response = await axios.get(`${BASE_URL}/Event/${eventId}`);
            const eventData = response.data;

            await axios.put(
                `${BASE_URL}/Event/${eventId}`,
                { ...eventData, exhibitors: updatedExhibitors }
            );

            if (onUpdate) onUpdate();
        } catch (error) {
            console.error('Error saving exhibitors:', error);
            throw error;
        }
    };

    const handleEditExhibitor = (exhibitor) => {
        setEditingExhibitor(exhibitor);
        form.setFieldsValue(exhibitor);
        setModalVisible(true);
    };

    const handleDeleteExhibitor = (exhibitorId) => {
        Modal.confirm({
            title: t('eventSections.exhibitors.delete'),
            content: t('eventSections.exhibitors.deleteConfirm'),
            okText: t('eventSections.common.delete'),
            okType: 'danger',
            onOk: async () => {
                try {
                    const updatedExhibitors = exhibitors.filter(e => e.id !== exhibitorId);

                    if (!canPersistToBackend) {
                        setExhibitors(updatedExhibitors);
                        if (onExhibitorsChange) onExhibitorsChange(updatedExhibitors);
                        message.success(t('eventSections.exhibitors.deleted'));
                        return;
                    }

                    await saveExhibitorsToBackend(updatedExhibitors);
                    setExhibitors(updatedExhibitors);
                    if (onExhibitorsChange) onExhibitorsChange(updatedExhibitors);
                    message.success(t('eventSections.exhibitors.deleted'));
                } catch (error) {
                    message.error(t('eventSections.exhibitors.deleteFailed'));
                }
            }
        });
    };


    const handleSendCard = async (exhibitor) => {
        if (!canPersistToBackend) {
            message.warning(t('eventSections.common.createEventFirst'));
            return;
        }

        try {
            message.loading({ content: t('eventSections.exhibitors.sendingWallet'), key: 'sendCard' });

            const response = await axios.post(`${BASE_URL}/EventWallet/send-card`, {
                type: "exhibitor",           // ✅ Specify type
                eventId: eventId,            // ✅ Event ID
                exhibitorId: exhibitor.id    // ✅ Exhibitor ID
            });

            if (response.data.success) {
                message.success({
                    content: t('eventSections.common.walletSent', { email: exhibitor.email }),
                    key: 'sendCard',
                    duration: 4
                });
            }
        } catch (error) {
            message.error({
                content: error.response?.data?.message || t('eventSections.common.walletSendFailed'),
                key: 'sendCard'
            });
        }
    };

    // --- REMOVED: Company logo upload logic (now managed in company settings) ---

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();

            const exhibitorId = editingExhibitor?.id || crypto.randomUUID();

            const exhibitorData = {
                id: exhibitorId,
                name: values.name,
                company: values.company,
                title: values.title,
                description: values.description,
                logoUrl: values.logoUrl,
                email: values.email,
                phone: values.phone,
                websiteUrl: values.websiteUrl,
                boothNumber: values.boothNumber,
                category: values.category,
                isFeatured: values.isFeatured || false,
                arabicName: values.arabicName,
                arabicCompany: values.arabicCompany,
                arabicDescription: values.arabicDescription,
                order: values.order || exhibitors.length,
                companyIconUrl: editingExhibitor?.companyIconUrl // Keep existing (no longer updated)
            };

            let updatedExhibitors;
            if (editingExhibitor) {
                updatedExhibitors = exhibitors.map(e => e.id === editingExhibitor.id ? exhibitorData : e);
            } else {
                updatedExhibitors = [...exhibitors, exhibitorData];
            }

            if (!canPersistToBackend) {
                setExhibitors(updatedExhibitors);
                if (onExhibitorsChange) onExhibitorsChange(updatedExhibitors);
                message.success(editingExhibitor ? t('eventSections.exhibitors.updated') : t('eventSections.exhibitors.added'));
                setModalVisible(false);
                form.resetFields();
                return;
            }

            await saveExhibitorsToBackend(updatedExhibitors);
            setExhibitors(updatedExhibitors);
            if (onExhibitorsChange) onExhibitorsChange(updatedExhibitors);
            message.success(editingExhibitor ? t('eventSections.exhibitors.updated') : t('eventSections.exhibitors.added'));
            setModalVisible(false);
            form.resetFields();
        } catch (error) {
            console.error('Error saving exhibitor:', error);
            message.error(t('eventSections.exhibitors.saveFailed'));
        }
    };

    const getMenuItems = (record) => [
                    {
                        key: 'edit',
                        icon: <EditOutlined />,
                        label: t('eventSections.exhibitors.edit'),
                        onClick: () => handleEditExhibitor(record),
                    },
                    {
                        key: 'send',
                        icon: <MailOutlined />,
                        label: t('eventSections.common.sendWalletPass'),
                        onClick: () => handleSendCard(record),
                    },
                    {
                        type: 'divider',
                    },
                    {
                        key: 'delete',
                        icon: <DeleteOutlined />,
                        label: t('eventSections.exhibitors.delete'),
                        danger: true,
                        onClick: () => handleDeleteExhibitor(record.id),
                    },
                ];

    const columns = [
        {
            title: t('eventSections.common.order'),
            dataIndex: 'order',
            width: 70,
            sorter: (a, b) => a.order - b.order,
            responsive: ['md'],
        },
        {
            title: t('eventSections.exhibitors.columns.exhibitor'),
            key: 'exhibitor',
            width: 250,
            ellipsis: true,
            render: (_, record) => (
                <Space>
                    <Avatar
                        src={record.logoUrl}
                        icon={<ShopOutlined />}
                        size={48}
                        shape="square"
                    />
                    <div>
                        <div className="font-medium">{record.name}</div>
                        <div className="text-gray-500 text-sm">{record.company}</div>
                        {record.boothNumber && (
                            <div className="text-gray-400 text-xs">
                                <EnvironmentOutlined /> {t('eventSections.exhibitors.booth', { number: record.boothNumber })}
                            </div>
                        )}
                    </div>
                </Space>
            )
        },
        {
            title: t('eventSections.exhibitors.columns.category'),
            dataIndex: 'category',
            key: 'category',
            width: 150,
            render: (category) => category && <Tag color="blue">{t(`eventSections.exhibitors.categories.${category}`, { defaultValue: category })}</Tag>
        },
        {
            title: t('eventSections.common.contact'),
            key: 'contact',
            width: 220,
            ellipsis: true,
            render: (_, record) => (
                <div>
                    {record.email && (
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px' }}>
                            <MailOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                            <span style={{ fontSize: '12px' }}>{record.email}</span>
                        </div>
                    )}
                    {record.phone && (
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <PhoneOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                            <span style={{ fontSize: '12px' }}>{record.phone}</span>
                        </div>
                    )}
                </div>
            )
        },
        {
            title: t('eventSections.common.website'),
            key: 'website',
            width: 100,
            align: 'center',
            render: (_, record) => (
                record.websiteUrl && <GlobalOutlined style={{ color: '#1890ff' }} />
            )
        },
        {
            title: t('eventSections.common.checkIn'),
            key: 'checkIn',
            width: 130,
            render: (_, record) => {
                // Match by Registration ID (Exhibitor ID)
                const isCheckedIn = checkIns.some(c =>
                    c.registrationId === record.id
                );
                return isCheckedIn ? (
                    <Tag color="green" icon={<CheckCircleOutlined />}>{t('eventSections.common.checkedIn')}</Tag>
                ) : (
                    <Tag icon={<CloseCircleOutlined />}>{t('eventSections.common.notYet')}</Tag>
                );
            }
        },
        {
            title: t('eventSections.common.status'),
            key: 'status',
            width: 120,
            render: (_, record) => (
                record.isFeatured && <Tag color="gold" icon={<StarOutlined />}>{t('eventSections.common.featured')}</Tag>
            )
        },
        {
            title: t('eventSections.common.actions'),
            key: 'actions',
            fixed: 'right',
            width: 80,
            render: (_, record) => (
                <Dropdown
                    menu={{
                        items: getMenuItems(record),
                    }}
                    trigger={['click']}
                >
                    <Button type="text" icon={<MoreOutlined style={{ fontSize: '18px' }} />} />
                </Dropdown>
            )
        }
    ];

    return (
        <Card
            title={t('eventSections.exhibitors.title')}
            extra={
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleAddExhibitor}
                >
                    {t('eventSections.exhibitors.add')}
                </Button>
            }
        >
            {isMobile ? (
                <EntityCardList
                    data={[...exhibitors].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))}
                    avatar={(r) => <Avatar src={r.logoUrl} icon={<ShopOutlined />} size={48} shape="square" style={{ flexShrink: 0 }} />}
                    title={(r) => r.company || r.name}
                    subtitle={(r) => [r.company ? r.name : null, r.boothNumber ? t('eventSections.exhibitors.booth', { number: r.boothNumber }) : null].filter(Boolean).join(' · ')}
                    tags={(r) => {
                        const isCheckedIn = checkIns.some((c) => c.registrationId === r.id);
                        return (
                            <>
                                {isCheckedIn ? (
                                    <Tag color="green" icon={<CheckCircleOutlined />}>{t('eventSections.common.checkedIn')}</Tag>
                                ) : (
                                    <Tag icon={<CloseCircleOutlined />}>{t('eventSections.common.notYet')}</Tag>
                                )}
                                {r.isFeatured && <Tag color="gold" icon={<StarOutlined />}>{t('eventSections.common.featured')}</Tag>}
                                {r.category && <Tag color="blue">{t(`eventSections.exhibitors.categories.${r.category}`, { defaultValue: r.category })}</Tag>}
                            </>
                        );
                    }}
                    details={(r) =>
                        (r.email || r.phone) ? (
                            <>
                                {r.email && <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}><MailOutlined style={{ color: '#1890ff' }} /> {r.email}</span>}
                                {r.phone && <span><PhoneOutlined style={{ color: '#1890ff' }} /> {r.phone}</span>}
                            </>
                        ) : null
                    }
                    menuItems={getMenuItems}
                    onTap={handleEditExhibitor}
                    emptyText={t('eventSections.exhibitors.empty')}
                />
            ) : (
            <Table
                dataSource={exhibitors}
                columns={columns}
                rowKey="id"
                scroll={{ x: 1200 }}
                pagination={false}
                tableLayout="auto"
                locale={{ emptyText: t('eventSections.exhibitors.empty') }}
            />
            )}

            <Modal
                title={editingExhibitor ? t('eventSections.exhibitors.edit') : t('eventSections.exhibitors.add')}
                open={modalVisible}
                onOk={handleSubmit}
                onCancel={() => {
                    setModalVisible(false);
                    form.resetFields();
                }}
                width={isMobile ? 'calc(100vw - 16px)' : 800}
                style={isMobile ? { top: 12 } : undefined}
                styles={isMobile ? { body: { maxHeight: '70vh', overflowY: 'auto', overflowX: 'hidden' } } : undefined}
                okText={editingExhibitor ? t('eventSections.common.update') : t('eventSections.common.add')}
            >
                <Form form={form} layout="vertical">
                    <Row gutter={16}>
                        <Col xs={24} sm={18}>
                            <Form.Item
                                name="name"
                                label={t('eventSections.exhibitors.form.contactName')}
                                rules={[{ required: true, message: t('eventSections.exhibitors.form.contactRequired') }]}
                            >
                                <Input placeholder={t('eventSections.exhibitors.form.contactPlaceholder')} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={6}>
                            <Form.Item
                                name="order"
                                label={t('eventSections.common.displayOrder')}
                                initialValue={exhibitors.length}
                            >
                                <Input type="number" min={0} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col xs={24} sm={12}>
                            <Form.Item
                                name="company"
                                label={t('eventSections.exhibitors.form.companyName')}
                                rules={[{ required: true, message: t('eventSections.exhibitors.form.companyRequired') }]}
                            >
                                <Input placeholder={t('eventSections.exhibitors.form.companyPlaceholder')} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item
                                name="title"
                                label={t('eventSections.exhibitors.form.position')}
                            >
                                <Input placeholder={t('eventSections.exhibitors.form.positionPlaceholder')} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        name="description"
                        label={t('eventSections.exhibitors.form.companyDescription')}
                    >
                        <TextArea rows={4} placeholder={t('eventSections.exhibitors.form.descriptionPlaceholder')} />
                    </Form.Item>

                    <Form.Item
                        name="logoUrl"
                        label={t('eventSections.exhibitors.form.logoUrl')}
                    >
                        <Input placeholder="https://example.com/logo.png" />
                    </Form.Item>

                    {/* <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item label="Company Logo (for Wallet Pass)">
                                <div style={{
                                    padding: '12px 16px',
                                    backgroundColor: '#e6f7ff',
                                    border: '1px solid #91d5ff',
                                    borderRadius: '4px',
                                    display: 'flex',
                                    alignItems: 'start'
                                }}>
                                    <span style={{ marginRight: '8px', fontSize: '16px' }}>ℹ️</span>
                                    <div>
                                        <div style={{ fontWeight: 500, marginBottom: '4px' }}>
                                            Company logos are managed centrally
                                        </div>
                                        <div style={{ fontSize: '12px', color: '#595959' }}>
                                            The company logo for wallet passes is automatically fetched from your company settings.
                                            To update it, go to Settings → Company Profile → Company Logo.
                                        </div>
                                    </div>
                                </div>
                            </Form.Item>
                        </Col>
                    </Row> */}

                    <Row gutter={16}>
                        <Col xs={24} sm={12}>
                            <Form.Item
                                name="email"
                                label={t('eventSections.common.email')}
                                rules={[{ type: 'email', message: t('eventSections.common.emailInvalid') }]}
                            >
                                <Input placeholder="contact@company.com" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item
                                name="phone"
                                label={t('eventSections.common.phone')}
                            >
                                <Input placeholder="+1 234 567 8900" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col xs={24} sm={12}>
                            <Form.Item
                                name="websiteUrl"
                                label={t('eventSections.common.website')}
                            >
                                <Input
                                    prefix={<GlobalOutlined />}
                                    placeholder="https://www.company.com"
                                />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item
                                name="boothNumber"
                                label={t('eventSections.exhibitors.form.boothNumber')}
                            >
                                <Input
                                    prefix={<EnvironmentOutlined />}
                                    placeholder={t('eventSections.exhibitors.form.boothPlaceholder')}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col xs={24} sm={12}>
                            <Form.Item
                                name="category"
                                label={t('eventSections.exhibitors.form.category')}
                            >
                                <Select placeholder={t('eventSections.exhibitors.form.selectCategory')}>
                                    <Select.Option value="Technology">{t('eventSections.exhibitors.categories.Technology')}</Select.Option>
                                    <Select.Option value="Healthcare">{t('eventSections.exhibitors.categories.Healthcare')}</Select.Option>
                                    <Select.Option value="Finance">{t('eventSections.exhibitors.categories.Finance')}</Select.Option>
                                    <Select.Option value="Education">{t('eventSections.exhibitors.categories.Education')}</Select.Option>
                                    <Select.Option value="Manufacturing">{t('eventSections.exhibitors.categories.Manufacturing')}</Select.Option>
                                    <Select.Option value="Services">{t('eventSections.exhibitors.categories.Services')}</Select.Option>
                                    <Select.Option value="Other">{t('eventSections.exhibitors.categories.Other')}</Select.Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Form.Item
                                name="isFeatured"
                                label={t('eventSections.exhibitors.form.featuredExhibitor')}
                                valuePropName="checked"
                            >
                                <Switch />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Card title={t('eventSections.common.arabicTranslation')} size="small">
                        <Form.Item
                            name="arabicName"
                            label={t('eventSections.common.arabicName')}
                        >
                            <Input placeholder="الاسم بالعربية" style={{ direction: 'rtl' }} />
                        </Form.Item>
                        <Form.Item
                            name="arabicCompany"
                            label={t('eventSections.exhibitors.form.arabicCompanyName')}
                        >
                            <Input placeholder="اسم الشركة" style={{ direction: 'rtl' }} />
                        </Form.Item>
                        <Form.Item
                            name="arabicDescription"
                            label={t('eventSections.common.arabicDescription')}
                        >
                            <TextArea rows={3} placeholder="الوصف بالعربية" style={{ direction: 'rtl' }} />
                        </Form.Item>
                    </Card>
                </Form>
            </Modal>
        </Card>
    );
};

export default ExhibitorManagement;
